import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, AlertCircle, Loader, Eye, EyeOff, KeyRound, CheckCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import { changePassword } from "../../services/auth.service";
import { useAuth } from "../../hooks/useAuth";
import { getToken, getMustChangePassword, getRole } from "../../utils/auth";
import { validateAllMessages, isPassword } from "../../utils/validation";
import { VALIDATION_MESSAGES, TOAST_MESSAGES } from "../../constants/messages";

const validationSchema = {
  currentPassword: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.password.required },
  ],
  newPassword: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.password.required },
    { test: (v: string) => v.length >= 8, message: VALIDATION_MESSAGES.password.minLength },
    isPassword(VALIDATION_MESSAGES.password.invalid),
  ],
  confirmPassword: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.confirmPassword.required },
  ],
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const { completePasswordChange, logout, user, tenant } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({
    currentPassword: [],
    newPassword: [],
    confirmPassword: [],
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // If there is no valid session, force a login first.
    if (!getToken()) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // Scenario 2: Success state after password update
  if (isSuccess) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-8 py-12">
          <div className="text-center">
            <img
              src="/logo/DiagnomIQ.png"
              alt="DiagnomIQ"
              className="mx-auto h-24 w-auto object-contain"
            />
          </div>
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">Password updated</h2>
              <p className="mt-2 text-sm text-gray-600">
                Your password has been changed successfully. You can now access the application.
              </p>
            </div>
            <div className="mt-6">
              <button
                onClick={() => navigate("/admin")}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Continue to dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Scenario 3: A user who has already completed the change has no business here.
  if (getToken() && !getMustChangePassword()) {
    return <Navigate to="/admin" replace />;
  }

  const validateField = (field: string, value: string): string[] => {
    const rules = validationSchema[field as keyof typeof validationSchema];
    if (!rules) return [];
    const fieldErrors: string[] = [];
    for (const rule of rules) {
      if (!rule.test(value)) {
        fieldErrors.push(rule.message);
      }
    }
    return fieldErrors;
  };

  const handleChange = (field: string, value: string) => {
    if (field === "currentPassword") setCurrentPassword(value);
    else if (field === "newPassword") setNewPassword(value);
    else setConfirmPassword(value);

    setApiError("");

    if (touched[field]) {
      const fieldErrors = validateField(field, value);
      if (field === "confirmPassword" && value !== newPassword) {
        fieldErrors.push(VALIDATION_MESSAGES.confirmPassword.mismatch);
      }
      setErrors((prev) => ({ ...prev, [field]: fieldErrors }));
    }
  };

  const handleBlur = (field: string) => {
    const value =
      field === "currentPassword" ? currentPassword : field === "newPassword" ? newPassword : confirmPassword;
    setTouched((prev) => ({ ...prev, [field]: true }));
    const fieldErrors = validateField(field, value);
    if (field === "confirmPassword" && value !== newPassword) {
      fieldErrors.push(VALIDATION_MESSAGES.confirmPassword.mismatch);
    }
    setErrors((prev) => ({ ...prev, [field]: fieldErrors }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    const allErrors = validateAllMessages({ currentPassword, newPassword, confirmPassword }, validationSchema);
    if (confirmPassword !== newPassword) {
      allErrors.confirmPassword = [
        ...(allErrors.confirmPassword || []),
        VALIDATION_MESSAGES.confirmPassword.mismatch,
      ];
    }
    setErrors(allErrors);
    setTouched({ currentPassword: true, newPassword: true, confirmPassword: true });

    const hasErrors = Object.values(allErrors).some((msgs) => msgs.length > 0);
    if (hasErrors) return;

    setIsLoading(true);
    try {
      const result = await changePassword(currentPassword, newPassword);
      // Scenario 2: password updated + first-login flag cleared (fresh token returned)
      completePasswordChange(result.token, getRole() || "", result.user || user, tenant);
      setIsSuccess(true);
      toast.success(TOAST_MESSAGES.success.changePassword);
    } catch (err: any) {
      const msg = err?.response?.data?.message || TOAST_MESSAGES.error.changePasswordFailed;
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full space-y-8 py-12">
        <div className="text-center">
          <img
            src="/logo/DiagnomIQ.png"
            alt="DiagnomIQ"
            className="mx-auto h-24 w-auto object-contain"
          />
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
            <KeyRound className="h-3.5 w-3.5" />
            First-time sign in
          </div>
          <h2 className="mt-4 text-xl font-bold text-gray-900">Change your password</h2>
          <p className="mt-2 text-sm text-gray-600">
            For your account's security, please set a new password before continuing.
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
          {apiError && (
            <div
              id="change-password-api-error"
              className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              role="alert"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {apiError}
            </div>
          )}

          <form id="change-password-form" className="space-y-6" onSubmit={handleSubmit} noValidate>
            {/* Current Password */}
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                Current Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => handleChange("currentPassword", e.target.value)}
                  onBlur={() => handleBlur("currentPassword")}
                  className={`pl-10 pr-10 py-3 block w-full rounded-md sm:text-sm transition-colors ${
                    touched.currentPassword && errors.currentPassword?.length
                      ? "border-red-300 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500"
                      : "border-neutral-300 focus:ring-primary-300 focus:border-primary-500"
                  }`}
                  placeholder="Enter your temporary password"
                  autoComplete="current-password"
                  aria-invalid={!!(touched.currentPassword && errors.currentPassword?.length)}
                  aria-describedby={errors.currentPassword?.length ? "currentPassword-error" : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                >
                  {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {touched.currentPassword && errors.currentPassword?.length > 0 && (
                <div id="currentPassword-error" className="mt-1 space-y-0.5" role="alert">
                  {errors.currentPassword.map((msg, i) => (
                    <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      {msg}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => handleChange("newPassword", e.target.value)}
                  onBlur={() => handleBlur("newPassword")}
                  className={`pl-10 pr-10 py-3 block w-full rounded-md sm:text-sm transition-colors ${
                    touched.newPassword && errors.newPassword?.length
                      ? "border-red-300 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500"
                      : "border-neutral-300 focus:ring-primary-300 focus:border-primary-500"
                  }`}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  aria-invalid={!!(touched.newPassword && errors.newPassword?.length)}
                  aria-describedby={errors.newPassword?.length ? "newPassword-error" : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {touched.newPassword && errors.newPassword?.length > 0 && (
                <div id="newPassword-error" className="mt-1 space-y-0.5" role="alert">
                  {errors.newPassword.map((msg, i) => (
                    <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      {msg}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  onBlur={() => handleBlur("confirmPassword")}
                  className={`pl-10 pr-10 py-3 block w-full rounded-md sm:text-sm transition-colors ${
                    touched.confirmPassword && errors.confirmPassword?.length
                      ? "border-red-300 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500"
                      : "border-neutral-300 focus:ring-primary-300 focus:border-primary-500"
                  }`}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  aria-invalid={!!(touched.confirmPassword && errors.confirmPassword?.length)}
                  aria-describedby={
                    errors.confirmPassword?.length ? "confirmPassword-error" : undefined
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {touched.confirmPassword && errors.confirmPassword?.length > 0 && (
                <div id="confirmPassword-error" className="mt-1 space-y-0.5" role="alert">
                  {errors.confirmPassword.map((msg, i) => (
                    <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      {msg}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                id="change-password-submit-btn"
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Updating password...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={logout}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
